import { lazy, Suspense } from 'react';
import { Navigate, RouterProvider, createBrowserRouter } from 'react-router-dom';

import './App.scss';
import Root from './pages/Root';
import ErrorPage from './pages/ErrorPage';
import { CONTRACTS_AI_ENABLED } from './config/features';

const Feed = lazy(() => import('./pages/Feed'));
const BuildingDetail = lazy(() => import('./pages/BuildingDetail'));
const Login = lazy(() => import('./pages/Login'));
const Signup = lazy(() => import('./pages/Signup'));
const Landing = lazy(() => import('./pages/Landing'));
const ServicePage = lazy(() => import('./pages/ServicePage'));
const AudiencePage = lazy(() => import('./pages/AudiencePage'));
const Locations = lazy(() => import('./pages/Locations'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Messages = lazy(() => import('./pages/Messages'));
const BuildingContacts = lazy(() => import('./pages/BuildingContacts'));
const BuildingDocuments = lazy(() => import('./pages/BuildingDocuments'));
const BuildingAsk = lazy(() => import('./pages/BuildingAsk'));
const BuildingAmenities = lazy(() => import('./pages/BuildingAmenities'));
const BuildingEvents = lazy(() => import('./pages/BuildingEvents'));
const BuildingInfo = lazy(() => import('./pages/BuildingInfo'));
const BuildingSafety = lazy(() => import('./pages/BuildingSafety'));
const Terms = lazy(() => import('./pages/Terms'));
const Privacy = lazy(() => import('./pages/Privacy'));
const About = lazy(() => import('./pages/About'));
const TagFeed = lazy(() => import('./pages/TagFeed'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const PostDetail = lazy(() => import('./pages/PostDetail'));
const MyProperties = lazy(() => import('./pages/MyProperties'));
const GlobalCalendar = lazy(() => import('./pages/GlobalCalendar'));
const BuildingSentiment = lazy(() => import('./pages/BuildingSentiment'));
const BuildingAccess = lazy(() => import('./pages/BuildingAccess'));
const NotificationsPage = lazy(() => import('./pages/Notifications'));
const Contracts = lazy(() => import('./pages/Contracts'));
const Crm = lazy(() => import('./pages/Crm/Crm'));
const CrmLeadDetail = lazy(() => import('./pages/Crm/LeadDetail'));

const router = createBrowserRouter([
  {
    path: '/terms',
    element: (
      <Suspense fallback={null}>
        <Terms />
      </Suspense>
    ),
  },
  {
    path: '/privacy',
    element: (
      <Suspense fallback={null}>
        <Privacy />
      </Suspense>
    ),
  },
  {
    path: '/about',
    element: (
      <Suspense fallback={null}>
        <About />
      </Suspense>
    ),
  },
  {
    path: '/login',
    element: (
      <Suspense fallback={null}>
        <Login />
      </Suspense>
    ),
    errorElement: <ErrorPage />,
  },
  // /register was the old PRO application form, a heavier duplicate of
  // /signup. The page is gone; the path redirects so old links and
  // bookmarks land on the form that replaced it (seoMiddleware 301s it
  // for crawlers).
  { path: '/register', element: <Navigate to="/signup" replace /> },
  {
    path: '/signup',
    element: (
      <Suspense fallback={null}>
        <Signup />
      </Suspense>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/',
    element: <Root />,
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={null}>
            <Landing />
          </Suspense>
        ),
      },
      // Locations pillar, and the three region pages beneath it. The
      // region URLs are the original local-SEO paths and stay where they
      // are — they were indexed when they rendered Landing with a swapped
      // hero, so moving them under /locations/ would have cost three
      // redirects for nothing. Copy for all four lives in
      // src/constants/regions.js, which seoMiddleware also require()s.
      {
        path: 'locations',
        element: (
          <Suspense fallback={null}>
            <Locations />
          </Suspense>
        ),
      },
      {
        path: 'central-coast',
        element: (
          <Suspense fallback={null}>
            <Locations region="central-coast" />
          </Suspense>
        ),
      },
      {
        path: 'newcastle',
        element: (
          <Suspense fallback={null}>
            <Locations region="newcastle" />
          </Suspense>
        ),
      },
      {
        path: 'sydney',
        element: (
          <Suspense fallback={null}>
            <Locations region="sydney" />
          </Suspense>
        ),
      },
      // Per-service landing pages — /cleaning, /building-management,
      // /grounds-and-gardens, /maintenance, /compliance, /platform. Each
      // is a distinct SEO document with its own primary keyword; the
      // homepage service cards link to the first three and the asides
      // under them carry the other three. /maintenance was pulled on
      // 2026-10-18 and came back on 2026-10-25, aimed at the
      // hard-to-reach work.
      //
      // Two of them were renamed to the words people search for. The old
      // paths stay reachable and redirect, below — a bare <Navigate> for
      // in-app clicks and stale bookmarks, plus a real 301 in
      // seoMiddleware so a crawler is told the address moved rather than
      // being served the page at both.
      {
        path: 'cleaning',
        element: (
          <Suspense fallback={null}>
            <ServicePage service="cleaning" />
          </Suspense>
        ),
      },
      {
        path: 'building-management',
        element: (
          <Suspense fallback={null}>
            <ServicePage service="caretaking" />
          </Suspense>
        ),
      },
      { path: 'caretaking', element: <Navigate to="/building-management" replace /> },
      {
        path: 'grounds-and-gardens',
        element: (
          <Suspense fallback={null}>
            <ServicePage service="grounds" />
          </Suspense>
        ),
      },
      { path: 'grounds', element: <Navigate to="/grounds-and-gardens" replace /> },
      {
        path: 'maintenance',
        element: (
          <Suspense fallback={null}>
            <ServicePage service="maintenance" />
          </Suspense>
        ),
      },
      {
        path: 'compliance',
        element: (
          <Suspense fallback={null}>
            <ServicePage service="compliance" />
          </Suspense>
        ),
      },
      {
        path: 'platform',
        element: (
          <Suspense fallback={null}>
            <ServicePage service="platform" />
          </Suspense>
        ),
      },
      // Audience landing pages. A service page says what we do; these
      // say who we do it for, which is the question a strata manager or
      // a short-stay owner actually searches. Copy lives in
      // src/constants/audiences.js, shared with the SSR middleware.
      {
        path: 'strata-managers',
        element: (
          <Suspense fallback={null}>
            <AudiencePage audience="strata-managers" />
          </Suspense>
        ),
      },
      {
        path: 'owners-corporations',
        element: (
          <Suspense fallback={null}>
            <AudiencePage audience="owners-corporations" />
          </Suspense>
        ),
      },
      {
        path: 'property-managers',
        element: (
          <Suspense fallback={null}>
            <AudiencePage audience="property-managers" />
          </Suspense>
        ),
      },
      {
        path: 'short-stay-accommodation',
        element: (
          <Suspense fallback={null}>
            <AudiencePage audience="short-stay-accommodation" />
          </Suspense>
        ),
      },
      // Retired: topics are per-building now and live on the building
      // page's Community tab. Redirect rather than delete outright, so an
      // old link lands on My Properties instead of falling through to the
      // ':userId' catch-all below, whose loader 500s on an unknown user.
      {
        path: 'topics',
        element: <Navigate to="/my" replace />,
      },
      {
        path: 'my',
        element: (
          <Suspense fallback={null}>
            <MyProperties />
          </Suspense>
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: 'calendar',
        element: (
          <Suspense fallback={null}>
            <GlobalCalendar />
          </Suspense>
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: 'notifications',
        element: (
          <Suspense fallback={null}>
            <NotificationsPage />
          </Suspense>
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: 'messages',
        element: (
          <Suspense fallback={null}>
            <Messages />
          </Suspense>
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: 'contracts',
        element: CONTRACTS_AI_ENABLED ? (
          <Suspense fallback={null}>
            <Contracts />
          </Suspense>
        ) : (
          <Navigate to="/" replace />
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: 'building/:buildingId',
        element: (
          <Suspense fallback={null}>
            <BuildingDetail />
          </Suspense>
        ),
        loader: ({ params }) =>
          import('./pages/BuildingDetail').then((module) => module.loader({ params })),
        errorElement: <ErrorPage />,
      },
      {
        path: 'admin',
        element: (
          <Suspense fallback={null}>
            <AdminDashboard />
          </Suspense>
        ),
        errorElement: <ErrorPage />,
      },
      // Two routes rather than /crm?lead=<id>, so a lead is linkable:
      // from the enquiry notification email, a push notification, or a
      // message to whoever is picking it up.
      {
        path: 'crm',
        element: (
          <Suspense fallback={null}>
            <Crm />
          </Suspense>
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: 'crm/lead/:leadId',
        element: (
          <Suspense fallback={null}>
            <CrmLeadDetail />
          </Suspense>
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: 'building/:buildingId/contacts',
        element: (
          <Suspense fallback={null}>
            <BuildingContacts />
          </Suspense>
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: 'building/:buildingId/documents',
        element: (
          <Suspense fallback={null}>
            <BuildingDocuments />
          </Suspense>
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: 'building/:buildingId/ask',
        element: (
          <Suspense fallback={null}>
            <BuildingAsk />
          </Suspense>
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: 'building/:buildingId/amenities',
        element: (
          <Suspense fallback={null}>
            <BuildingAmenities />
          </Suspense>
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: 'building/:buildingId/events',
        element: (
          <Suspense fallback={null}>
            <BuildingEvents />
          </Suspense>
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: 'building/:buildingId/info',
        element: (
          <Suspense fallback={null}>
            <BuildingInfo />
          </Suspense>
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: 'building/:buildingId/sentiment',
        element: (
          <Suspense fallback={null}>
            <BuildingSentiment />
          </Suspense>
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: 'building/:buildingId/access',
        element: (
          <Suspense fallback={null}>
            <BuildingAccess />
          </Suspense>
        ),
        errorElement: <ErrorPage />,
      },
      // Safety page — temporarily disabled
      // {
      //   path: 'building/:buildingId/safety',
      //   element: (
      //     <Suspense fallback={null}>
      //       <BuildingSafety />
      //     </Suspense>
      //   ),
      //   errorElement: <ErrorPage />,
      // },
      {
        path: 'tag/:tag',
        element: (
          <Suspense fallback={null}>
            <TagFeed />
          </Suspense>
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: 'post/:postId',
        element: (
          <Suspense fallback={null}>
            <PostDetail />
          </Suspense>
        ),
        loader: ({ params }) =>
          import('./pages/PostDetail').then((module) => module.loader({ params })),
        errorElement: <ErrorPage />,
      },
      {
        path: '@:username',
        element: (
          <Suspense fallback={null}>
            <Feed />
          </Suspense>
        ),
        loader: ({ params }) =>
          import('./pages/Feed').then((module) => module.loader({ params: { userId: '@' + params.username } })),
        errorElement: <ErrorPage />,
      },
      {
        path: ':userId',
        element: (
          <Suspense fallback={null}>
            <Feed />
          </Suspense>
        ),
        loader: ({ params }) =>
          import('./pages/Feed').then((module) => module.loader({ params })),
        errorElement: <ErrorPage />,
      },
    ],
  },
  {
    path: '*',
    element: (
      <Suspense fallback={null}>
        <NotFound />
      </Suspense>
    ),
  },
], {
  // Opt in to the v7 behaviour early so the console stops warning about
  // it and we're already on the eventual default before upgrading.
  // Wraps router state updates in React.startTransition, which lets
  // Suspense fallbacks for lazy() routes render smoothly instead of
  // blocking the previous screen.
  future: {
    v7_startTransition: true,
  },
});

function App() {
  // Also set the flag on the provider — react-router 6.x only checks
  // renderFuture for v7_startTransition when deciding whether to log
  // the deprecation warning, so the router-level future option isn't
  // enough on its own.
  return <RouterProvider router={router} future={{ v7_startTransition: true }} />;
}

export default App;
